import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Folder } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { AttachmentPreviewGrid } from '@/components/shared/AttachmentPreviewGrid'
import { ConfirmDeleteDialog } from '@/components/shared/ConfirmDeleteDialog'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import { ListPagination } from '@/components/shared/ListPagination'
import {
  TECHNICIAN_DETAIL_TAB_STALE_TIME,
  useTechnicianDocuments,
} from '@/hooks/useTechnicians'
import { useListParams } from '@/hooks/useListParams'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { techniciansApi } from '@/services/technicians'
import { DOCUMENT_FILE_ACCEPT, isDocumentFileExtension } from '@/lib/constants'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import { cn } from '@/lib/utils'
import { usePermissions } from '@/hooks/usePermissions'
import type { AttachmentRef } from '@/components/shared'
import type { Documentable } from '@/types'

const cardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'

// 2.10.6 Technician details — attachments card, styled to match the detail
// mockups rather than the generic embedded table chrome.
export function DocumentsTab({ technicianId }: { technicianId: string }) {
  const { t } = useTranslation(['clients', 'common', 'technicians', 'shared'])
  const qc = useQueryClient()
  const { canEdit } = usePermissions()
  const readOnly = !canEdit('technicians')
  const { page, setPage, limit, setLimit, query } = useListParams()
  const { data, isLoading, isError } = useTechnicianDocuments(
    technicianId,
    query,
    { staleTime: TECHNICIAN_DETAIL_TAB_STALE_TIME }
  )
  const inputRef = useRef<HTMLInputElement>(null)
  const [deleteTarget, setDeleteTarget] = useState<Documentable | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)

  const refresh = () =>
    qc.invalidateQueries({
      queryKey: ['technicians', technicianId, 'documents'],
    })
  const refreshCount = () =>
    qc.invalidateQueries({
      queryKey: ['technicians', 'detail', technicianId],
    })

  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length || readOnly || isUploading) return

    let uploaded = 0
    setIsUploading(true)
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
        if (!isDocumentFileExtension(ext)) {
          toast({
            variant: 'destructive',
            title: t('documents.unsupportedFormat'),
          })
          continue
        }
        if (isFileTooLarge(file)) {
          toast({
            variant: 'destructive',
            title: t('shared:attachFiles.tooLarge', {
              max: getFileSizeLimitMb(),
            }),
          })
          continue
        }

        try {
          await techniciansApi.uploadDocument(technicianId, file)
          uploaded += 1
        } catch (err) {
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(err, t('documents.uploadError')),
          })
        }
      }

      if (uploaded > 0) {
        toast({ variant: 'success', title: t('documents.uploadSuccess') })
        await Promise.all([refresh(), refreshCount()])
      }
    } finally {
      setIsUploading(false)
    }
  }

  const renameDocument = async (item: AttachmentRef, name: string) => {
    if (!item.id || readOnly) return
    await techniciansApi.renameDocument(technicianId, item.id, name)
    toast({ variant: 'success', title: t('documents.renameSuccess') })
    await refresh()
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await techniciansApi.deleteDocument(technicianId, deleteTarget.id)
      toast({ variant: 'success', title: t('documents.deleteSuccess') })
      void Promise.all([refresh(), refreshCount()])
      setDeleteTarget(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(err, t('documents.deleteError')),
      })
    }
  }

  const documents = useMemo(
    () => (data?.data ?? []).filter(document => document.doc_type !== 'avatar'),
    [data?.data]
  )
  const attachmentItems = useMemo(
    () =>
      documents.map(document => ({
        id: document.id,
        name: document.file_name,
        url: document.url,
        mime_type: document.file_type,
        size: document.file_size,
        created_at: document.created_at,
        source: 'upload' as const,
      })),
    [documents]
  )

  return (
    <section className={cardClass}>
      <div className='flex min-h-16 items-start gap-4 px-6 pb-2 pt-6'>
        <div className='flex items-center gap-3'>
          <div className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#fbf3d9] text-[#a16207]'>
            <Folder className='h-4 w-4' />
          </div>
          <div className='min-w-0'>
            <h3 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
              {t('detail.attachmentsTitle', { ns: 'technicians' })}
            </h3>
          </div>
        </div>
      </div>

      <div className='space-y-4 px-6 pb-6 pt-4'>
        {!readOnly ? (
          <div className='space-y-2'>
            <label className='block text-xs font-medium leading-4 text-[#272c36]'>
              {t('shared:attachFiles.uploadLabel')}{' '}
              <span className='text-[#ff3b30]'>*</span>
            </label>
            <input
              ref={inputRef}
              type='file'
              multiple
              className='hidden'
              accept={DOCUMENT_FILE_ACCEPT}
              onChange={event => {
                void uploadFiles(event.target.files)
                event.target.value = ''
              }}
            />
            <FileUploadDropzone
              primaryText={
                <>
                  <span className='block'>
                    {t('shared:attachFiles.uploadHintPrimary')}
                  </span>
                  <span className='block'>
                    {t('form.documentsFormatHint', { ns: 'technicians' })}
                  </span>
                </>
              }
              loading={isUploading}
              loadingText={t('documents.uploading')}
              dragActive={isDragActive}
              disabled={isUploading}
              className='min-h-[72px] rounded-xl border-[#e7e9ef] p-4 [&>span:last-child>span]:leading-4'
              onClick={() => inputRef.current?.click()}
              onDragOver={event => {
                event.preventDefault()
                setIsDragActive(true)
              }}
              onDragLeave={event => {
                event.preventDefault()
                setIsDragActive(false)
              }}
              onDrop={event => {
                event.preventDefault()
                setIsDragActive(false)
                void uploadFiles(event.dataTransfer.files)
              }}
            />
          </div>
        ) : null}

        {isLoading ? (
          <div className='space-y-3'>
            {Array.from({ length: 2 }).map((_, index) => (
              <div
                key={index}
                className='h-[62px] animate-pulse rounded-2xl bg-[#f4f5f8]'
              />
            ))}
          </div>
        ) : isError ? (
          <div className='rounded-2xl bg-[#fff1f0] px-4 py-4 text-sm leading-[1.4] text-destructive'>
            {t('documents.error')}
          </div>
        ) : documents.length === 0 ? (
          <div className='flex min-h-11 items-center gap-2 rounded-xl border border-[#e7e9ef] bg-[#f4f5f8] p-3 text-sm font-medium leading-[1.4] text-[#555b6a]'>
            <FileText className='size-4 shrink-0 text-[#6c7285]' />
            <span>{t('detail.noAttachmentYet', { ns: 'technicians' })}</span>
          </div>
        ) : (
          <>
            <AttachmentPreviewGrid
              items={attachmentItems}
              removeLabel={t('documents.actions.delete')}
              onRename={readOnly ? undefined : renameDocument}
              onRemove={
                readOnly
                  ? undefined
                  : item => {
                      const target = documents.find(
                        document => document.id === item.id
                      )
                      if (target) setDeleteTarget(target)
                    }
              }
              onItemClick={item => {
                if (item.url) {
                  window.open(item.url, '_blank', 'noopener')
                }
              }}
              itemClickLabel={t('documents.actions.download')}
            />

            {(data?.metadata?.last_page ?? 1) > 1 ? (
              <div
                className={cn(
                  '-mx-6 border-t border-[#e7e9ef] px-4 pt-2',
                  documents.length > 0 && 'mt-2'
                )}
              >
                <ListPagination
                  metadata={data?.metadata}
                  page={page}
                  pageSize={limit}
                  onPageChange={setPage}
                  onPageSizeChange={setLimit}
                  showShowingRange={false}
                />
              </div>
            ) : null}
          </>
        )}
      </div>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={o => !o && setDeleteTarget(null)}
        title={t('documents.deleteDialog.title')}
        description={t('documents.deleteDialog.confirm')}
        confirmLabel={t('common:actions.confirm')}
        cancelLabel={t('common:actions.cancel')}
        onConfirm={confirmDelete}
      />
    </section>
  )
}

export default DocumentsTab
